import React, { Component } from "react";
import clsx from "clsx";

import BoModal from "../common/BoModal";
import GmailService from "../../../services/GmailService";
import UtilityService from "../../../services/UtilityService";
import { I } from "../common/icons";

const { alert } = UtilityService;

/**
 * Connect or disconnect the signed-in user's Gmail account, which backoffice automations send from.
 * Uses the per-user Gmail connection (Settings › User Preferences shares it).
 */
class EmailAccountModal extends Component {
	state = { busy: false, status: null };

	async componentDidMount() {
		// The page may still be loading when this opens, so ask for the current status directly.
		const status = await GmailService.getStatus();
		this.setState({ status: status || { connected: false, configured: false, gmailAddress: null } });
	}

	refresh = async () => {
		const status = await GmailService.getStatus();
		if (status) this.setState({ status });
		await this.props.onChanged();
	};

	connect = async () => {
		this.setState({ busy: true });
		try {
			// getUserAuthUrl resolves to the response body, { url }, or null when the API refused.
			const response = await GmailService.getUserAuthUrl();
			const url = response && (typeof response === "string" ? response : response.url);
			if (!url) {
				alert({ type: "error", text: (response && response.error) || "Could not start Google sign-in. Google sign-in may not be set up on this server." });
				return;
			}
			const result = await GmailService.openConsentPopup({ url });
			if (result.ok) {
				alert({ type: "success", text: `Gmail connected: ${result.gmailAddress}` });
				await this.refresh();
			} else {
				alert({ type: "error", text: `Gmail not connected: ${result.message || "consent cancelled"}` });
			}
		} finally {
			this.setState({ busy: false });
		}
	};

	resyncTasks = async () => {
		this.setState({ resyncing: true, resyncResult: null });
		const result = await GmailService.resyncTasks();
		this.setState({ resyncing: false, resyncResult: result });
		if (result.ok && this.props.onTasksResynced) this.props.onTasksResynced(result);
	};

	disconnect = async () => {
		this.setState({ busy: true });
		try {
			const result = await GmailService.disconnectUser();
			if (result && (result.disconnected || result.ok)) {
				alert({ type: "success", text: "Disconnected. Email automations will send through Workflow mail until you reconnect." });
				await this.refresh();
			} else {
				alert({ type: "error", text: "Could not disconnect Gmail." });
			}
		} finally {
			this.setState({ busy: false });
		}
	};

	render() {
		const { onClose } = this.props;
		const { busy, status } = this.state;
		const gmailStatus = status || this.props.gmailStatus;
		const loaded = !!status;
		const connected = gmailStatus && gmailStatus.connected;
		// Older API builds don't report `configured`; treat a missing flag as configured.
		const configured = loaded && gmailStatus.configured !== false;
		// Tasks sync needs the Tasks scope, which connections made before it was requested do not have.
		const tasksEnabled = connected && gmailStatus.tasksEnabled === true;
		const { resyncing, resyncResult } = this.state;
		return (
			<BoModal
				title="Email account"
				icon={<I.link />}
				onClose={onClose}
				footer={
					<>
						{connected && (
							<button className="bo-btn bo-btn--danger" type="button" onClick={this.disconnect} disabled={busy}>
								Disconnect
							</button>
						)}
						<button className="bo-btn" type="button" onClick={onClose}>
							Close
						</button>
						<button className="bo-btn bo-btn--primary" type="button" onClick={this.connect} disabled={busy || !configured}>
							{busy ? "Opening Google…" : connected ? "Connect a different account" : "Connect Google account"}
						</button>
					</>
				}
			>
				<div className={clsx("bo-acct", { "bo-acct--off": !connected })}>
					<div className="bo-avatar bo-avatar--g">G</div>
					<div className="bo-acct__who">
						<b>{connected ? gmailStatus.gmailAddress : "No email account connected"}</b>
						<span>{connected ? "Google Workspace · Connected · token refreshes automatically" : "Automations send through Workflow's mail service, with your address as reply-to, until you connect."}</span>
					</div>
					<span className={clsx("bo-pill", connected ? "bo-pill--green" : "bo-pill--red")}>{connected ? "Connected" : "Not connected"}</span>
				</div>
				{connected && (
					<div className={clsx("bo-acct", "bo-acct--tasks", { "bo-acct--off": !tasksEnabled })}>
						<div className="bo-avatar bo-avatar--g">
							<I.check size={16} />
						</div>
						<div className="bo-acct__who">
							<b>Google Tasks</b>
							<span>{tasksEnabled ? "On. Backoffice tasks you prepare are mirrored into your \"Workflow Backoffice\" list as they change, and ticked off when you complete them here." : "Not enabled for this connection. Connect your Google account again and allow Google Tasks to turn it on."}</span>
						</div>
						<span className={clsx("bo-pill", tasksEnabled ? "bo-pill--green" : "bo-pill--amber")}>{tasksEnabled ? "On" : "Reconnect to enable"}</span>
						{tasksEnabled && (
							<button className="bo-btn bo-btn--sm ft-bo-tasks-resync" type="button" onClick={this.resyncTasks} disabled={resyncing} title="Push every open task you prepare that is not in Google Tasks yet, including ones whose last push failed">
								<I.refresh />
								{resyncing ? "Syncing…" : "Retry sync"}
							</button>
						)}
					</div>
				)}
				{resyncResult && (
					<div className={clsx("bo-callout", { "bo-callout--blue": resyncResult.ok })} style={{ marginBottom: 10 }}>
						<div className="bo-callout__note ft-bo-tasks-resync-result">{resyncResult.ok
								? resyncResult.attempted === 0
									? `Nothing to push: all ${resyncResult.total} open task${resyncResult.total === 1 ? " you prepare is" : "s you prepare are"} already in Google Tasks.`
									: `Pushed ${resyncResult.synced} of ${resyncResult.attempted} task${resyncResult.attempted === 1 ? "" : "s"} that needed it${resyncResult.alreadySynced ? ` (${resyncResult.alreadySynced} already there)` : ""}.${resyncResult.failed ? " Tasks that still failed show an amber Google Tasks chip with the reason." : ""}`
								: resyncResult.error}</div>
					</div>
				)}
				<div>
					<div style={{ fontWeight: 700, marginBottom: 8 }}>{connected ? "Connect a different Google account" : "Connect your Google account"}</div>
					{loaded && !configured && (
						<div className="bo-callout" style={{ marginBottom: 10 }}>
							<h4>
								<I.alert />
								Google sign-in isn't set up on this server
							</h4>
							<div className="bo-callout__note">An administrator needs to add the Gmail OAuth client to the API configuration. Until then, automations send through Workflow's mail service with your address as reply-to.</div>
						</div>
					)}
					<button className={clsx("bo-prov-card", { "bo-prov-card--on": connected })} type="button" onClick={this.connect} disabled={busy || !configured}>
						<span className="bo-prov-card__logo">G</span>
						<b>Google Workspace / Gmail</b>
						<span>Sign in with Google. Uses the Gmail API to send mail as you. Restricted to the firm's Workspace domain.</span>
					</button>
				</div>
				<div className="bo-callout bo-callout--blue">
					<h4>What Workflow will do with this account</h4>
					<div className="bo-perm">
						<div>
							<I.check size={16} />
							Send client request emails and follow-ups from your address, on the schedule you set
						</div>
						<div>
							<I.check size={16} />
							Keep each send in your Gmail Sent folder so replies land in your inbox
						</div>
						<div>
							<I.check size={16} />
							Stop an automation when its task is marked complete
						</div>
						<div>
							<I.check size={16} />
							Mirror the backoffice tasks you prepare into your Google Tasks, one way, and complete them there when you complete them here
						</div>
						<div>
							<I.check size={16} />
							Never reads your other mail. Never sends without an active automation.
						</div>
					</div>
				</div>
			</BoModal>
		);
	}
}

export default EmailAccountModal;
